import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {styled} from 'common'
import {useTranslation} from 'react-i18next'
import {Box, Stack, Typography} from '@mui/material'
import Tooltip from 'components/Display/Tooltip/Tooltip'
import {Colors} from 'appearance'

// ─── Constants ────────────────────────────────────────────────────────────────

export const getRateCalendarMonths = (locale = 'en', format = 'short') =>
    Array.from({length: 12}, (_, i) => new Date(2000, i, 1).toLocaleString(locale, {month: format}))

const MONTH_PERIOD = 12
const HOUR_PERIOD = 24

// ─── Pure helpers ─────────────────────────────────────────────────────────────

const mod = (value, period) => {
    const result = value % period
    return result < 0 ? result + period : result
}

const toCellKey = (monthIndex, hourIndex) =>
    `${mod(monthIndex, MONTH_PERIOD)}:${mod(hourIndex, HOUR_PERIOD)}`

const resolveIndex = (nextIndex, lastValue, period) => {
    const lastNormalized = mod(lastValue, period)
    let delta = nextIndex - lastNormalized
    const threshold = period / 2
    if (delta > threshold) delta -= period
    else if (delta < -threshold) delta += period
    return lastValue + delta
}

const isValueInCyclicRange = (value, start, end, period) => {
    if (!Number.isFinite(value) || !Number.isFinite(start) || !Number.isFinite(end)) return false
    if (start > end) return false
    return Math.ceil((start - value) / period) <= Math.floor((end - value) / period)
}

const isValueOnBoundary = (value, boundary, period) => {
    if (!Number.isFinite(value) || !Number.isFinite(boundary)) return false
    return mod(boundary - value, period) === 0
}

const buildRect = (start, end) => {
    if (!start || !end) return null
    return {
        monthStart: Math.min(start.monthIndex, end.monthIndex),
        monthEnd: Math.max(start.monthIndex, end.monthIndex),
        hourStart: Math.min(start.hourIndex, end.hourIndex),
        hourEnd: Math.max(start.hourIndex, end.hourIndex),
    }
}

const getCellState = ({
    monthIndex,
    hourIndex,
    selectedCells,
    lockedCells,
    hoveredLockedCells,
    dragRect,
}) => {
    const key = `${monthIndex}:${hourIndex}`
    const isLocked = lockedCells.has(key)
    const isSelected = selectedCells.has(key)
    const isHoveredLocked = hoveredLockedCells.has(key)
    const isActiveSelection = isLocked || isSelected || isHoveredLocked

    if (!dragRect) {
        return {
            isLocked,
            isSelected,
            isHoveredLocked,
            isActiveSelection,
            isPreview: false,
            isCurrent: false,
            isLeftEdge: false,
            isRightEdge: false,
            isTopEdge: false,
            isBottomEdge: false,
            isAnchor: false,
        }
    }

    const isCurrent =
        isValueInCyclicRange(monthIndex, dragRect.monthStart, dragRect.monthEnd, MONTH_PERIOD) &&
        isValueInCyclicRange(hourIndex, dragRect.hourStart, dragRect.hourEnd, HOUR_PERIOD)

    const isAnchor =
        (monthIndex === mod(dragRect.monthStart, MONTH_PERIOD) &&
            hourIndex === mod(dragRect.hourStart, HOUR_PERIOD)) ||
        (monthIndex === mod(dragRect.monthEnd, MONTH_PERIOD) &&
            hourIndex === mod(dragRect.hourEnd, HOUR_PERIOD))

    return {
        isLocked,
        isSelected,
        isHoveredLocked,
        isActiveSelection,
        isCurrent,
        isPreview: isCurrent && !isLocked,
        isLeftEdge: isCurrent && isValueOnBoundary(monthIndex, dragRect.monthStart, MONTH_PERIOD),
        isRightEdge: isCurrent && isValueOnBoundary(monthIndex, dragRect.monthEnd, MONTH_PERIOD),
        isTopEdge: isCurrent && isValueOnBoundary(hourIndex, dragRect.hourStart, HOUR_PERIOD),
        isBottomEdge: isCurrent && isValueOnBoundary(hourIndex, dragRect.hourEnd, HOUR_PERIOD),
        isAnchor,
    }
}

// ─── useDragSelection ─────────────────────────────────────────────────────────

const useDragSelection = ({
    gridRef,
    isReadOnly,
    lockedCells,
    onRectAction,
    onLockedCellClick,
    onLockedCellHover,
    onLockedCellLeave,
}) => {
    const [dragRect, setDragRectState] = useState(null)
    const dragRectRef = useRef(null)
    const dragStartRef = useRef(null)
    const dragModeRef = useRef('add')
    const lastResolvedRef = useRef(null)
    const metricsRef = useRef(null)

    const setDragRect = useCallback((val) => {
        dragRectRef.current = val
        setDragRectState(val)
    }, [])

    const computeMetrics = useCallback(() => {
        const el = gridRef.current
        if (!el) return null
        const firstCell = el.querySelector('[data-month-index="0"][data-hour-index="0"]')
        if (!firstCell) return null
        const r = firstCell.getBoundingClientRect()
        return {originLeft: r.left, originTop: r.top, cellWidth: r.width, cellHeight: r.height}
    }, [gridRef])

    const getIndicesFromClient = useCallback(
        (clientX, clientY) => {
            const target = document.elementFromPoint(clientX, clientY)
            if (
                target?.dataset?.monthIndex !== undefined &&
                target?.dataset?.hourIndex !== undefined
            ) {
                return {
                    monthIndex: Number(target.dataset.monthIndex),
                    hourIndex: Number(target.dataset.hourIndex),
                }
            }
            const metrics = metricsRef.current || computeMetrics()
            if (!metrics?.cellWidth || !metrics?.cellHeight) return null
            const rawMonth = Math.floor((clientX - metrics.originLeft) / metrics.cellWidth)
            const rawHour = Math.floor((clientY - metrics.originTop) / metrics.cellHeight)
            if (!Number.isFinite(rawMonth) || !Number.isFinite(rawHour)) return null
            return {monthIndex: rawMonth, hourIndex: rawHour}
        },
        [computeMetrics]
    )

    const resetDrag = useCallback(() => {
        dragStartRef.current = null
        dragModeRef.current = 'add'
        lastResolvedRef.current = null
        metricsRef.current = null
        setDragRect(null)
    }, [setDragRect])

    const startSelection = useCallback(
        (monthIndex, hourIndex, mode = 'add') => {
            if (isReadOnly || lockedCells.has(toCellKey(monthIndex, hourIndex))) return
            const startPoint = {monthIndex, hourIndex}
            dragStartRef.current = startPoint
            dragModeRef.current = mode
            lastResolvedRef.current = {month: monthIndex, hour: hourIndex}
            metricsRef.current = computeMetrics()
            setDragRect(buildRect(startPoint, startPoint))
        },
        [computeMetrics, isReadOnly, lockedCells, setDragRect]
    )

    const applyDragRect = useCallback(
        (monthIndex, hourIndex) => {
            if (!dragStartRef.current || !lastResolvedRef.current) return
            const resolvedMonth = resolveIndex(
                monthIndex,
                lastResolvedRef.current.month,
                MONTH_PERIOD
            )
            const resolvedHour = resolveIndex(hourIndex, lastResolvedRef.current.hour, HOUR_PERIOD)
            lastResolvedRef.current = {month: resolvedMonth, hour: resolvedHour}
            setDragRect(
                buildRect(dragStartRef.current, {
                    monthIndex: resolvedMonth,
                    hourIndex: resolvedHour,
                })
            )
        },
        [setDragRect]
    )

    const finishDrag = useCallback(() => {
        if (!isReadOnly && dragRectRef.current && onRectAction) {
            onRectAction(dragRectRef.current, dragModeRef.current || 'add')
        }
        resetDrag()
    }, [isReadOnly, onRectAction, resetDrag])

    useEffect(() => {
        if (isReadOnly) return
        window.addEventListener('mouseup', finishDrag)
        window.addEventListener('touchend', finishDrag)
        window.addEventListener('touchcancel', finishDrag)
        return () => {
            window.removeEventListener('mouseup', finishDrag)
            window.removeEventListener('touchend', finishDrag)
            window.removeEventListener('touchcancel', finishDrag)
        }
    }, [finishDrag, isReadOnly])

    useEffect(() => {
        if (isReadOnly) return
        const handleMouseMove = (e) => {
            if (!dragStartRef.current) return
            const indices = getIndicesFromClient(e.clientX, e.clientY)
            if (indices) applyDragRect(indices.monthIndex, indices.hourIndex)
        }
        window.addEventListener('mousemove', handleMouseMove)
        return () => window.removeEventListener('mousemove', handleMouseMove)
    }, [applyDragRect, getIndicesFromClient, isReadOnly])

    const handleMouseDown = useCallback(
        (event) => {
            event.preventDefault()
            const monthValue = Number(event.currentTarget.dataset.monthIndex)
            const hourValue = Number(event.currentTarget.dataset.hourIndex)
            const key = toCellKey(monthValue, hourValue)
            if (lockedCells.has(key)) {
                onLockedCellHover?.(key)
                if (event.button === 0) onLockedCellClick?.(key)
                return
            }
            onLockedCellLeave?.()
            if (!isReadOnly)
                startSelection(monthValue, hourValue, event.button === 2 ? 'remove' : 'add')
        },
        [
            isReadOnly,
            lockedCells,
            onLockedCellClick,
            onLockedCellHover,
            onLockedCellLeave,
            startSelection,
        ]
    )

    const handleMouseEnter = useCallback(
        (event) => {
            const {monthIndex, hourIndex} = event.currentTarget.dataset
            if (monthIndex == null || hourIndex == null) return
            const key = toCellKey(Number(monthIndex), Number(hourIndex))
            if (lockedCells.has(key)) {
                onLockedCellHover?.(key)
                return
            }
            onLockedCellLeave?.()
            if (!isReadOnly && dragStartRef.current)
                applyDragRect(Number(monthIndex), Number(hourIndex))
        },
        [applyDragRect, isReadOnly, lockedCells, onLockedCellHover, onLockedCellLeave]
    )

    const handleMouseLeave = useCallback(
        (event) => {
            const {monthIndex, hourIndex} = event.currentTarget.dataset
            if (monthIndex == null || hourIndex == null) return
            if (lockedCells.has(toCellKey(Number(monthIndex), Number(hourIndex))))
                onLockedCellLeave?.()
        },
        [lockedCells, onLockedCellLeave]
    )

    const handleTouchStart = useCallback(
        (event) => {
            const {monthIndex, hourIndex} = event.currentTarget.dataset
            if (monthIndex == null || hourIndex == null) return
            event.preventDefault()
            const monthValue = Number(monthIndex)
            const hourValue = Number(hourIndex)
            const key = toCellKey(monthValue, hourValue)
            if (lockedCells.has(key)) {
                onLockedCellHover?.(key)
                onLockedCellClick?.(key)
                return
            }
            onLockedCellLeave?.()
            if (!isReadOnly) startSelection(monthValue, hourValue, 'add')
        },
        [
            isReadOnly,
            lockedCells,
            onLockedCellClick,
            onLockedCellHover,
            onLockedCellLeave,
            startSelection,
        ]
    )

    const handleTouchMove = useCallback(
        (event) => {
            if (isReadOnly || !dragStartRef.current) return
            const touch = event.touches[0]
            if (!touch) return
            event.preventDefault()
            const indices = getIndicesFromClient(touch.clientX, touch.clientY)
            if (!indices) return
            const key = toCellKey(indices.monthIndex, indices.hourIndex)
            if (lockedCells.has(key)) {
                onLockedCellHover?.(key)
                return
            }
            onLockedCellLeave?.()
            applyDragRect(indices.monthIndex, indices.hourIndex)
        },
        [
            applyDragRect,
            getIndicesFromClient,
            isReadOnly,
            lockedCells,
            onLockedCellHover,
            onLockedCellLeave,
        ]
    )

    const handleKeyDown = useCallback(
        (event) => {
            const {monthIndex, hourIndex} = event.currentTarget.dataset
            if (monthIndex == null || hourIndex == null) return
            const monthValue = Number(monthIndex)
            const hourValue = Number(hourIndex)
            const key = toCellKey(monthValue, hourValue)
            if (lockedCells.has(key)) {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onLockedCellHover?.(key)
                    onLockedCellClick?.(key)
                }
                return
            }
            if ((event.key === 'Enter' || event.key === ' ') && !isReadOnly && onRectAction) {
                event.preventDefault()
                const point = {monthIndex: monthValue, hourIndex: hourValue}
                onRectAction(buildRect(point, point), event.shiftKey ? 'remove' : 'add')
                resetDrag()
            }
        },
        [isReadOnly, lockedCells, onLockedCellClick, onLockedCellHover, onRectAction, resetDrag]
    )

    return {
        dragRect,
        dragMode: dragModeRef.current || 'add',
        handleMouseDown,
        handleMouseEnter,
        handleMouseLeave,
        handleTouchStart,
        handleTouchMove,
        handleKeyDown,
    }
}

// ─── CalendarCell ─────────────────────────────────────────────────────────────

const CalendarCell = React.memo(
    ({
        monthIndex,
        hourIndex,
        monthLabel,
        hourLabel,
        selectedCells,
        lockedCells,
        hoveredLockedCells,
        dragRect,
        dragMode,
        isReadOnly,
        lockedCellDetails,
        onMouseDown,
        onMouseEnter,
        onMouseLeave,
        onTouchStart,
        onKeyDown,
        t,
    }) => {
        const {
            isLocked,
            isPreview,
            isCurrent,
            isActiveSelection,
            isHoveredLocked,
            isLeftEdge,
            isRightEdge,
            isTopEdge,
            isBottomEdge,
            isAnchor,
        } = getCellState({
            monthIndex,
            hourIndex,
            selectedCells,
            lockedCells,
            hoveredLockedCells,
            dragRect,
        })

        const isFocusableCell =
            !isReadOnly &&
            !isLocked &&
            (isAnchor || (!selectedCells.size && monthIndex === 0 && hourIndex === 0))

        const tabIndex = isFocusableCell ? 0 : -1

        const cellElement = (
            <TimeCell
                data-month-index={monthIndex}
                data-hour-index={hourIndex}
                role="gridcell"
                aria-label={t('rates.calendar.aria.cell', {month: monthLabel, hour: hourLabel})}
                aria-selected={isActiveSelection || isPreview}
                aria-disabled={isLocked}
                tabIndex={tabIndex}
                onMouseDown={onMouseDown}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onTouchStart={onTouchStart}
                onKeyDown={onKeyDown}
                onContextMenu={(e) => e.preventDefault()}
                $isCurrent={isCurrent && !isLocked}
                $isPreview={isPreview}
                $previewMode={dragMode}
                $isActiveSelection={isActiveSelection}
                $isHoveredLocked={isHoveredLocked}
                $isLocked={isLocked}
                $isEdge={isLeftEdge || isRightEdge || isTopEdge || isBottomEdge}
                $isLeftEdge={isLeftEdge}
                $isRightEdge={isRightEdge}
                $isTopEdge={isTopEdge}
                $isBottomEdge={isBottomEdge}
                $isAnchor={isAnchor}
            />
        )

        const rateForCell = isLocked ? lockedCellDetails?.get?.(`${monthIndex}:${hourIndex}`) : null
        if (!rateForCell) return cellElement

        const rateValue = rateForCell.rate != null ? rateForCell.rate : '—'
        const tooltipTitle = (
            <Box sx={{p: 0.5, minWidth: 150}}>
                <Typography
                    sx={{
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        opacity: 0.6,
                        mb: 0.75,
                    }}>
                    {t('rates.calendar.lockedRate.tooltipTitle')}
                </Typography>
                <Typography sx={{fontWeight: 700, fontSize: '0.82rem', lineHeight: 1.3, mb: 1}}>
                    {rateForCell.name || '—'}
                </Typography>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap'}}>
                    <Box
                        sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: '6px',
                            bgcolor: 'rgba(99,102,241,0.25)',
                            border: '1px solid rgba(99,102,241,0.4)',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                        }}>
                        {rateValue}
                    </Box>
                    <Typography sx={{fontSize: '0.7rem', opacity: 0.7}}>
                        {t(`selectOptions.${rateForCell.typeDay || 'weekday'}`)}
                    </Typography>
                </Box>
            </Box>
        )

        return (
            <Tooltip
                title={tooltipTitle}
                placement="top"
                arrow
                enterDelay={150}
                enterNextDelay={50}
                componentsProps={{
                    tooltip: {
                        sx: {
                            bgcolor: 'grey.900',
                            maxWidth: 240,
                            p: 1.25,
                            borderRadius: 2,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                        },
                    },
                    arrow: {sx: {color: 'grey.900'}},
                }}>
                <span>{cellElement}</span>
            </Tooltip>
        )
    }
)

// ─── RateCalendarGrid ─────────────────────────────────────────────────────────

const RateCalendarGrid = ({
    selectedCells = new Set(),
    lockedCells = new Set(),
    hoveredLockedCells = new Set(),
    onRectAction,
    onLockedCellClick,
    onLockedCellHover,
    onLockedCellLeave,
    lockedCellDetails,
    isReadOnly = false,
}) => {
    const {t, i18n} = useTranslation()
    const gridRef = useRef(null)
    const monthLabels = useMemo(() => getRateCalendarMonths(i18n.language, 'long'), [i18n.language])

    const {dragRect, dragMode, handleTouchMove, ...cellHandlers} = useDragSelection({
        gridRef,
        isReadOnly,
        lockedCells,
        onRectAction,
        onLockedCellClick,
        onLockedCellHover,
        onLockedCellLeave,
    })

    return (
        <Container role="grid" aria-label={t('rates.calendar.aria.grid')}>
            <TopBar>
                <TopBarTitle>{t('rates.calendar.gridTitle')}</TopBarTitle>
                <Legend>
                    <LegendItem>
                        <LegendSwatch variant="active" />
                        <LegendLabel>{t('rates.calendar.legend.active')}</LegendLabel>
                    </LegendItem>
                    <LegendItem>
                        <LegendSwatch variant="preview" mode="add" />
                        <LegendLabel>{t('rates.calendar.legend.previewAdd')}</LegendLabel>
                    </LegendItem>
                    <LegendItem>
                        <LegendSwatch variant="preview" mode="remove" />
                        <LegendLabel>{t('rates.calendar.legend.previewRemove')}</LegendLabel>
                    </LegendItem>
                </Legend>
            </TopBar>

            <HeaderRow role="row">
                <CornerCell role="columnheader">{t('rates.hours')}</CornerCell>
                {monthLabels.map((month, i) => (
                    <HeaderCell key={i} role="columnheader">
                        <HeaderCellLabel title={month}>{month}</HeaderCellLabel>
                    </HeaderCell>
                ))}
            </HeaderRow>

            <Body ref={gridRef} onTouchMove={handleTouchMove} role="rowgroup">
                {Array.from({length: HOUR_PERIOD}, (_, hourIndex) => {
                    const hourLabel = `${String(hourIndex).padStart(2, '0')}:00`
                    return (
                        <Row key={hourIndex} role="row">
                            <HourCell role="rowheader">{hourLabel}</HourCell>
                            {monthLabels.map((monthLabel, monthIndex) => (
                                <CalendarCell
                                    key={monthIndex}
                                    monthIndex={monthIndex}
                                    hourIndex={hourIndex}
                                    monthLabel={monthLabel}
                                    hourLabel={hourLabel}
                                    selectedCells={selectedCells}
                                    lockedCells={lockedCells}
                                    hoveredLockedCells={hoveredLockedCells}
                                    dragRect={dragRect}
                                    dragMode={dragMode}
                                    isReadOnly={isReadOnly}
                                    lockedCellDetails={lockedCellDetails}
                                    onMouseDown={cellHandlers.handleMouseDown}
                                    onMouseEnter={cellHandlers.handleMouseEnter}
                                    onMouseLeave={cellHandlers.handleMouseLeave}
                                    onTouchStart={cellHandlers.handleTouchStart}
                                    onKeyDown={cellHandlers.handleKeyDown}
                                    t={t}
                                />
                            ))}
                        </Row>
                    )
                })}
            </Body>
        </Container>
    )
}

export default RateCalendarGrid

// ─── Styled components ────────────────────────────────────────────────────────

const GRID_COLUMNS = '72px repeat(12, minmax(0, 1fr))'

const Container = styled.div`
    display: flex;
    flex-direction: column;
    border-radius: 12px;
    background-color: ${Colors.light};
    border: 1px solid ${Colors.LIGHT_GRAY};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
    overflow: hidden;
    user-select: none;
`

const TopBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    padding: 14px 18px;
    background-color: ${Colors.light};
    border-bottom: 1px solid ${Colors.LIGHT_GRAY};
`

const TopBarTitle = styled.span`
    font-weight: 600;
    font-size: 0.875rem;
    letter-spacing: 0.01em;
    color: ${Colors.DARK_CHARCOAL};
`

const Legend = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
`

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 0.72rem;
    font-weight: 500;
    color: ${Colors.APP_FADED_GREY};
    white-space: nowrap;
`

const LegendLabel = styled.span``

const LegendSwatch = styled.span
    .withConfig({shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'mode'})
    .attrs({role: 'presentation'})`
    width: 10px;
    height: 10px;
    border-radius: 3px;
    flex-shrink: 0;
    background: ${({variant, mode}) => {
        if (variant === 'active') return 'rgba(99, 102, 241, 0.75)'
        if (mode === 'remove') return 'rgba(239, 68, 68, 0.65)'
        return 'rgba(99, 102, 241, 0.35)'
    }};
    outline: 1px solid ${({variant, mode}) => {
        if (mode === 'remove') return 'rgba(239, 68, 68, 0.5)'
        return 'rgba(99, 102, 241, 0.35)'
    }};
`

const HeaderRow = styled.div`
    position: sticky;
    top: 0;
    z-index: 3;
    display: grid;
    grid-template-columns: ${GRID_COLUMNS};
    background-color: ${Colors.background};
    border-bottom: 1px solid ${Colors.LIGHT_GRAY};
`

const HeaderCell = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 9px 4px;
    font-weight: 600;
    font-size: 0.65rem;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: ${Colors.APP_FADED_GREY};
    border-inline-start: 1px solid ${Colors.LIGHT_GRAY};
    text-align: center;
`

const HeaderCellLabel = styled.span`
    display: block;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const CornerCell = styled(HeaderCell)`
    border-inline-start: none;
    justify-content: flex-end;
    padding-inline-end: 10px;
    font-size: 0.6rem;
`

const Body = styled.div`
    max-height: 460px;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: ${Colors.LIGHT_GRAY} transparent;

    &::-webkit-scrollbar {
        width: 5px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: ${Colors.LIGHT_GRAY};
        border-radius: 999px;
    }
`

const Row = styled.div`
    display: grid;
    grid-template-columns: ${GRID_COLUMNS};

    &:nth-child(odd) {
        background-color: ${Colors.light};
    }
    &:nth-child(even) {
        background-color: ${Colors.background};
    }

    &:last-child > * {
        border-bottom: none;
    }
`

const HourCell = styled.div`
    position: sticky;
    inset-inline-start: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-inline-end: 10px;
    font-weight: 600;
    font-size: 0.68rem;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.03em;
    color: ${Colors.APP_FADED_GREY};
    background-color: inherit;
    border-top: 1px solid ${Colors.LIGHT_GRAY};
    min-height: 36px;
`

const TimeCell = styled.div`
    height: 36px;
    border-top: 1px solid ${Colors.LIGHT_GRAY};
    border-inline-start: 1px solid ${Colors.LIGHT_GRAY};
    cursor: ${({$isLocked}) => ($isLocked ? 'default' : 'crosshair')};
    position: relative;
    transition: background 0.1s ease;
    background: ${({
        $isPreview,
        $previewMode,
        $isCurrent,
        $isActiveSelection,
        $isLocked,
        $isHoveredLocked,
    }) => {
        if ($isLocked) {
            return $isHoveredLocked ? 'rgba(99, 102, 241, 0.28)' : 'rgba(99, 102, 241, 0.14)'
        }
        if ($isPreview) {
            return $previewMode === 'remove'
                ? 'rgba(239, 68, 68, 0.22)'
                : 'rgba(99, 102, 241, 0.22)'
        }
        if ($isCurrent) return 'rgba(99, 102, 241, 0.16)'
        if ($isActiveSelection) return 'rgba(99, 102, 241, 0.1)'
        return 'transparent'
    }};

    &:hover {
        background: ${({
            $isCurrent,
            $isActiveSelection,
            $previewMode,
            $isLocked,
            $isHoveredLocked,
        }) => {
            if ($isLocked)
                return $isHoveredLocked ? 'rgba(99, 102, 241, 0.34)' : 'rgba(99, 102, 241, 0.22)'
            if ($isCurrent || $isActiveSelection) return 'rgba(99, 102, 241, 0.24)'
            return $previewMode === 'remove'
                ? 'rgba(239, 68, 68, 0.15)'
                : 'rgba(99, 102, 241, 0.07)'
        }};
    }

    ${({$isEdge}) =>
        $isEdge && `box-shadow: inset 0 0 0 1.5px rgba(99, 102, 241, 0.5); z-index: 1;`}
    ${({$isLeftEdge}) => $isLeftEdge && `border-inline-start: 1.5px solid rgba(99, 102, 241, 0.5);`}
    ${({$isRightEdge}) => $isRightEdge && `border-inline-end: 1.5px solid rgba(99, 102, 241, 0.5);`}
    ${({$isTopEdge}) => $isTopEdge && `border-top: 1.5px solid rgba(99, 102, 241, 0.5);`}
    ${({$isBottomEdge}) => $isBottomEdge && `border-bottom: 1.5px solid rgba(99, 102, 241, 0.5);`}

    ${({$isAnchor}) =>
        $isAnchor &&
        `
        &::after {
            content: '';
            position: absolute;
            inset: 4px;
            border-radius: 4px;
            border: 2px solid rgba(99, 102, 241, 0.7);
        }
    `}

    &:focus-visible {
        outline: 2px solid rgba(99, 102, 241, 0.6);
        outline-offset: -2px;
        z-index: 2;
    }
`
